/* =============================================================
   Contract Sign Page — Public page for clients to sign contracts
   URL: /contract/:token
   ============================================================= */
import { useState, useRef } from "react";
import { useParams } from "wouter";
import { trpc } from "@/lib/trpc";
import {
  Loader2, CheckCircle2, FileText, AlertCircle, Building2, Phone, Download
} from "lucide-react";

export default function ContractSignPage() {
  const { token } = useParams<{ token: string }>();
  const [clientName, setClientName] = useState("");
  const [clientPhone, setClientPhone] = useState("");
  const [clientEmail, setClientEmail] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [signed, setSigned] = useState(false);
  const [error, setError] = useState("");
  const contractRef = useRef<HTMLDivElement>(null);

  const downloadPdf = async () => {
    if (typeof window === "undefined") return;
    // @ts-ignore
    const html2pdf = (await import("html2pdf.js")).default;
    const element = document.getElementById("contract-print-area");
    if (!element) return;
    html2pdf().set({
      margin: [15, 15, 15, 15],
      filename: `gerree-${contract?.serviceName ?? "contract"}.pdf`,
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: { scale: 2 },
      jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
    }).from(element).save();
  };

  const { data: contract, isLoading, error: fetchError } = trpc.contracts.getByToken.useQuery(
    { token: token ?? "" },
    { enabled: !!token }
  );

  const signMutation = trpc.contracts.sign.useMutation({
    onSuccess: () => setSigned(true),
    onError: (err) => setError(err.message),
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (!clientName.trim()) { setError("Нэрээ оруулна уу."); return; }
    if (!clientPhone.trim()) { setError("Утасны дугаараа оруулна уу."); return; }
    if (!clientEmail.trim()) { setError("Имэйл хаягаа оруулна уу."); return; }
    if (!agreed) { setError("Гэрээний нөхцөлтэй зөвшөөрч байгаагаа тэмдэглэнэ үү."); return; }
    signMutation.mutate({ token: token ?? "", clientName, clientPhone, clientEmail });
  };

  const formatAmount = (n: number) => n.toLocaleString("mn-MN") + "₮";

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin text-blue-600 mx-auto mb-3" />
          <p className="text-slate-500">Гэрээ ачааллаж байна...</p>
        </div>
      </div>
    );
  }

  if (fetchError || !contract) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-slate-800 mb-2">Гэрээ олдсонгүй</h2>
          <p className="text-slate-500 text-sm">Линк буруу эсвэл хугацаа дууссан байж болно. United Dream Mongolia-тай холбогдоно уу.</p>
          <div className="mt-6 flex flex-col gap-2 text-sm text-slate-600">
            <a href="tel:80806633" className="flex items-center justify-center gap-2 hover:text-blue-600">
              <Phone className="w-4 h-4" /> 8080-6633
            </a>
            <a href="tel:90926633" className="flex items-center justify-center gap-2 hover:text-blue-600">
              <Phone className="w-4 h-4" /> 9092-6633
            </a>
          </div>
        </div>
      </div>
    );
  }

  if (contract.status === "cancelled") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
          <AlertCircle className="w-12 h-12 text-orange-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-slate-800 mb-2">Гэрээ цуцлагдсан</h2>
          <p className="text-slate-500 text-sm">Энэ гэрээ цуцлагдсан байна. Дэлгэрэнгүй мэдээлэл авахыг хүсвэл холбогдоно уу.</p>
        </div>
      </div>
    );
  }

  if (signed || contract.status === "signed") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
          <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-slate-800 mb-2">Гэрээ амжилттай байгуулагдлаа!</h2>
          <p className="text-slate-600 text-sm mb-4">
            <strong>{contract.serviceName}</strong> үйлчилгээний гэрээ баталгаажлаа.
            Баталгаажуулалтын имэйл таны хаяг руу явуулсан.
          </p>
          <div className="bg-green-50 rounded-xl p-4 text-left text-sm mb-6">
            {contract.paymentType === "full" ? (
              <p className="text-slate-700"><strong>Нийт төлбөр:</strong> {formatAmount(contract.totalAmount)}</p>
            ) : (
              <>
                <p className="text-slate-700"><strong>Нийт төлбөр:</strong> {formatAmount(contract.totalAmount)}</p>
                <p className="text-slate-700"><strong>Урьдчилгаа:</strong> {formatAmount(contract.advanceAmount ?? 0)}</p>
                <p className="text-slate-700"><strong>Үлдэгдэл:</strong> {formatAmount(contract.remainingAmount ?? 0)}</p>
              </>
            )}
          </div>
          <button
            onClick={downloadPdf}
            className="mt-4 flex items-center gap-2 mx-auto px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-xl transition-colors"
          >
            <Download className="w-4 h-4" /> Гэрээг PDF татах
          </button>
          <p className="text-xs text-slate-400 mt-4">Асуулт байвал: 8080-6633, 9092-6633</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 py-8 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-900 to-blue-700 rounded-2xl p-6 mb-6 text-white text-center">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Building2 className="w-6 h-6" />
            <span className="font-bold text-lg">United Dream Mongolia</span>
          </div>
          <h1 className="text-2xl font-bold mb-1">{contract.serviceName}</h1>
          <p className="text-blue-200 text-sm">Үйлчилгээний гэрээ</p>
        </div>

        {/* Payment Summary */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-5 mb-6">
          <h3 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
            <FileText className="w-4 h-4 text-blue-600" />
            Төлбөрийн мэдээлэл
          </h3>
          {contract.paymentType === "full" ? (
            <div className="flex justify-between items-center">
              <span className="text-slate-600">Нийт төлбөр (бүхлээр):</span>
              <span className="text-xl font-bold text-blue-700">{formatAmount(contract.totalAmount)}</span>
            </div>
          ) : (
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-slate-600">Нийт төлбөр:</span>
                <span className="font-bold text-slate-800">{formatAmount(contract.totalAmount)}</span>
              </div>
              <div className="flex justify-between border-t pt-2">
                <span className="text-slate-600">Урьдчилгаа:</span>
                <span className="font-bold text-red-600">{formatAmount(contract.advanceAmount ?? 0)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600">Үлдэгдэл:</span>
                <span className="font-bold text-slate-800">{formatAmount(contract.remainingAmount ?? 0)}</span>
              </div>
            </div>
          )}
        </div>

        {/* Contract Body */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 mb-6">
          <h3 className="font-semibold text-slate-700 mb-4 flex items-center gap-2">
            <FileText className="w-4 h-4 text-blue-600" />
            Гэрээний нөхцөл
          </h3>
          <div
            id="contract-print-area"
            className="prose prose-sm max-w-none text-slate-700 leading-relaxed contract-body"
          >
            <h2 style={{textAlign:"center"}}>{contract.serviceName}</h2>
            <p style={{textAlign:"center",color:"#64748b",fontSize:"12px"}}>Огноо: {new Date(contract.sentAt).toLocaleDateString("mn-MN")} | Имэйл: {contract.clientEmail}</p>
            <hr/>
            <div dangerouslySetInnerHTML={{ __html: contract.bodyHtml }} />
          </div>
        </div>

        {/* Signature Form */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
          <h3 className="font-semibold text-slate-700 mb-4">Гарын үсэг зурах</h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Таны бүтэн нэр *</label>
              <input
                type="text"
                value={clientName}
                onChange={e => setClientName(e.target.value)}
                placeholder="Жишээ нь: Батбаяр Дорж"
                className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Утасны дугаар *</label>
              <input
                type="tel"
                value={clientPhone}
                onChange={e => setClientPhone(e.target.value)}
                placeholder="Жишээ нь: 9911-2233"
                className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Имэйл хаяг *</label>
              <input
                type="email"
                value={clientEmail}
                onChange={e => setClientEmail(e.target.value)}
                placeholder="example@gmail.com"
                className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Agreement checkbox */}
            <div className="flex items-start gap-3 bg-blue-50 rounded-xl p-4">
              <input
                type="checkbox"
                id="agree"
                checked={agreed}
                onChange={e => setAgreed(e.target.checked)}
                className="mt-0.5 w-4 h-4 accent-blue-600 cursor-pointer flex-shrink-0"
              />
              <label htmlFor="agree" className="text-sm text-slate-700 cursor-pointer leading-relaxed">
                Дээрх гэрээний нөхцөлтэй бүрэн танилцаж, зөвшөөрч байгаагаа баталж байна. Энэхүү зөвшөөрөл нь цахим гарын үсэгтэй адилтгана.
              </label>
            </div>

            {error && (
              <div className="flex items-center gap-2 text-red-600 bg-red-50 rounded-lg px-4 py-3 text-sm">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={signMutation.isPending || !agreed}
              className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold py-3 rounded-xl text-base transition-colors flex items-center justify-center gap-2"
            >
              {signMutation.isPending ? (
                <><Loader2 className="w-5 h-5 animate-spin" /> Боловсруулж байна...</>
              ) : (
                <><CheckCircle2 className="w-5 h-5" /> Гэрээ байгуулах — Зөвшөөрч байна</>
              )}
            </button>
          </form>
        </div>

        {/* Footer */}
        <div className="text-center mt-6 text-xs text-slate-400">
          <p>United Dream Mongolia · СУИС-ийн урд, Gem Castle төв, 907 тоот</p>
          <p className="mt-1">8080-6633 · 9092-6633 · dreamvisamgl.com</p>
        </div>
      </div>
    </div>
  );
}
